import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotifications } from '@connect-x-app/hybrid-state';
import { Text } from '@connect-x-app/hybrid-ui';
import { SideModal, Spinner } from '@connect-x-app/web-ui';
import { hasReachedScrollEnd } from '@connect-x-app/web-utils';

import { NotificationItem } from './components/NotificationItem';

import './notificationCenter.scss';

type NotificationCenterProps = {
  isVisible: boolean;
  onClose: () => void;
};

export const NotificationCenter = ({ isVisible, onClose }: NotificationCenterProps) => {
  const { t } = useTranslation();
  const {
    data,
    error,
    fetchOlderNotifications,
    hasOlderNotifications,
    isFetchingNotifications,
    notifications,
    setNotifications,
  } = useNotifications();

  useEffect(() => {
    if (!data) {
      return;
    }
    const items = data.flatMap(p => p.items ?? []);
    setNotifications(items, data[0]?.unreadCount ?? 0);
  }, [data, setNotifications]);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (hasReachedScrollEnd(e) && !isFetchingNotifications && hasOlderNotifications) {
      fetchOlderNotifications();
    }
  };

  const renderNotificationsList = () => {
    return (
      <section className="notification-center-content-container" onScroll={handleScroll}>
        {notifications.map(notification => (
          <NotificationItem key={notification.id} notification={notification} onClick={onClose} />
        ))}
      </section>
    );
  };

  const renderContent = () => {
    if (isFetchingNotifications && notifications.length === 0) {
      return (
        <div className="loading-indicator">
          <Spinner size="large" />
        </div>
      );
    }

    if (error) {
      return (
        <Text className="notification-center-text" size="MD">
          {t('ERRORS.GENERAL')}
        </Text>
      );
    }

    if (notifications.length === 0) {
      return (
        <Text className="notification-center-text" size="MD">
          {t('NOTIFICATION_CENTER.EMPTY_NOTIFICATIONS')}
        </Text>
      );
    }

    return renderNotificationsList();
  };

  return (
    <SideModal
      className="notification-center-side-modal"
      content={renderContent()}
      fullwidth
      header={{
        leftIcon: { iconName: 'arrowLeft', onClick: onClose },
        title: { props: { uppercase: false }, text: t('NOTIFICATION_CENTER.TITLE') },
      }}
      isVisible={isVisible}
      onBackgroundClick={onClose}
      placement="left"
    />
  );
};
